const MIN_GEOMETRY_MAGNITUDE = 1e-9;
const SHA256_PATTERN = /^[a-f0-9]{64}$/;
function assertNonEmptyString(name, value) {
    if (value.trim().length === 0) {
        throw new RangeError(`${name} must not be empty`);
    }
}
function assertFiniteVector(name, vector) {
    for (const [axis, value] of Object.entries(vector)) {
        if (!Number.isFinite(value)) {
            throw new RangeError(`${name}.${axis} must be finite`);
        }
    }
}
function subtract(left, right) {
    return {
        x: left.x - right.x,
        y: left.y - right.y,
        z: left.z - right.z,
    };
}
function cross(left, right) {
    return {
        x: left.y * right.z - left.z * right.y,
        y: left.z * right.x - left.x * right.z,
        z: left.x * right.y - left.y * right.x,
    };
}
function magnitude(vector) {
    return Math.hypot(vector.x, vector.y, vector.z);
}
function assertUniqueIds(name, values) {
    const ids = new Set();
    for (const value of values) {
        assertNonEmptyString(`${name}.id`, value.id);
        if (ids.has(value.id)) {
            throw new RangeError(`${name} contains duplicate id ${value.id}`);
        }
        ids.add(value.id);
    }
}
function assertBoundary(name, boundary) {
    if (boundary.length < 3) {
        throw new RangeError(`${name} must contain at least three vertices`);
    }
    boundary.forEach((vertex, index) => {
        assertFiniteVector(`${name}[${index}]`, vertex);
    });
}
export function validateMapPhysicsAsset(asset) {
    assertNonEmptyString("asset.mapId", asset.mapId);
    assertNonEmptyString("asset.mapRevision", asset.mapRevision);
    assertNonEmptyString("asset.assetVersion", asset.assetVersion);
    assertFiniteVector("asset.radarTransform.originHu", asset.radarTransform.originHu);
    assertFiniteVector("asset.radarTransform.xAxisHu", asset.radarTransform.xAxisHu);
    assertFiniteVector("asset.radarTransform.yAxisHu", asset.radarTransform.yAxisHu);
    if (magnitude(asset.radarTransform.xAxisHu) <= MIN_GEOMETRY_MAGNITUDE ||
        magnitude(asset.radarTransform.yAxisHu) <= MIN_GEOMETRY_MAGNITUDE ||
        magnitude(cross(asset.radarTransform.xAxisHu, asset.radarTransform.yAxisHu)) <= MIN_GEOMETRY_MAGNITUDE) {
        throw new RangeError("asset.radarTransform axes must be independent");
    }
    if (asset.layers.length === 0) {
        throw new RangeError("asset.layers must not be empty");
    }
    if (asset.materials.length === 0) {
        throw new RangeError("asset.materials must not be empty");
    }
    if (asset.surfaces.length === 0) {
        throw new RangeError("asset.surfaces must not be empty");
    }
    assertUniqueIds("asset.layers", asset.layers);
    assertUniqueIds("asset.materials", asset.materials);
    assertUniqueIds("asset.surfaces", asset.surfaces);
    assertUniqueIds("asset.openings", asset.openings);
    assertUniqueIds("asset.portals", asset.portals);
    if (asset.layers.length > 1 && asset.portals.length === 0) {
        throw new RangeError("multi-layer assets must define at least one layer portal");
    }
    const layerIds = new Set(asset.layers.map((layer) => layer.id));
    const materialIds = new Set(asset.materials.map((material) => material.id));
    for (const layer of asset.layers) {
        assertNonEmptyString(`asset.layers.${layer.id}.label`, layer.label);
        if (!Number.isFinite(layer.minZHu) ||
            !Number.isFinite(layer.maxZHu) ||
            layer.minZHu > layer.maxZHu) {
            throw new RangeError(`asset.layers.${layer.id} has invalid Z bounds`);
        }
    }
    for (const material of asset.materials) {
        if (!Number.isFinite(material.restitution) ||
            material.restitution < 0 ||
            material.restitution > 1) {
            throw new RangeError(`asset.materials.${material.id}.restitution must be between 0 and 1`);
        }
        if (!Number.isFinite(material.friction) ||
            material.friction < 0 ||
            material.friction > 1) {
            throw new RangeError(`asset.materials.${material.id}.friction must be between 0 and 1`);
        }
    }
    for (const surface of asset.surfaces) {
        if (!layerIds.has(surface.layerId)) {
            throw new RangeError(`asset.surfaces.${surface.id} references unknown layer ${surface.layerId}`);
        }
        if (!materialIds.has(surface.materialId)) {
            throw new RangeError(`asset.surfaces.${surface.id} references unknown material ${surface.materialId}`);
        }
        surface.vertices.forEach((vertex, index) => {
            assertFiniteVector(`asset.surfaces.${surface.id}.vertices[${index}]`, vertex);
        });
        const [a, b, c] = surface.vertices;
        if (magnitude(cross(subtract(b, a), subtract(c, a))) <= MIN_GEOMETRY_MAGNITUDE) {
            throw new RangeError(`asset.surfaces.${surface.id} triangle must not be degenerate`);
        }
        if (surface.kind !== "wall" && (a.z !== b.z || a.z !== c.z)) {
            throw new RangeError(`asset.surfaces.${surface.id} ${surface.kind} must be horizontal`);
        }
    }
    for (const opening of asset.openings) {
        if (!layerIds.has(opening.layerId)) {
            throw new RangeError(`asset.openings.${opening.id} references unknown layer ${opening.layerId}`);
        }
        assertBoundary(`asset.openings.${opening.id}.boundary`, opening.boundary);
    }
    for (const portal of asset.portals) {
        if (!layerIds.has(portal.fromLayerId)) {
            throw new RangeError(`asset.portals.${portal.id} references unknown from-layer ${portal.fromLayerId}`);
        }
        if (!layerIds.has(portal.toLayerId)) {
            throw new RangeError(`asset.portals.${portal.id} references unknown to-layer ${portal.toLayerId}`);
        }
        if (portal.fromLayerId === portal.toLayerId) {
            throw new RangeError(`asset.portals.${portal.id} must connect different layers`);
        }
        assertBoundary(`asset.portals.${portal.id}.boundary`, portal.boundary);
    }
}
export function validateMapPhysicsAssetManifest(manifest) {
    assertNonEmptyString("manifest.radar.path", manifest.radar.path);
    if (!SHA256_PATTERN.test(manifest.radar.sha256)) {
        throw new RangeError("manifest.radar.sha256 must be a SHA-256 hash");
    }
    if (!Number.isInteger(manifest.radar.widthPixels) ||
        manifest.radar.widthPixels <= 0 ||
        !Number.isInteger(manifest.radar.heightPixels) ||
        manifest.radar.heightPixels <= 0) {
        throw new RangeError("manifest.radar dimensions must be positive integers");
    }
    assertNonEmptyString("manifest.source.manifestPath", manifest.source.manifestPath);
    assertNonEmptyString("manifest.source.cs2BuildId", manifest.source.cs2BuildId);
    if (!Number.isInteger(manifest.source.triangleCount) ||
        manifest.source.triangleCount <= 0) {
        throw new RangeError("manifest.source.triangleCount must be a positive integer");
    }
    if (manifest.status === "setup_required") {
        assertNonEmptyString("manifest.mapId", manifest.mapId);
        assertNonEmptyString("manifest.mapRevision", manifest.mapRevision);
        if (manifest.missing.length === 0) {
            throw new RangeError("setup_required manifest must list missing requirements");
        }
        return;
    }
    validateMapPhysicsAsset(manifest.asset);
}
